import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ExternalLink, FileText, Info } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { useSession } from '@/shared/auth/SessionProvider';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';
import { MANUALES, helpForPath, notasParaPais, pasosParaPais } from './moduleHelp';

/**
 * Ayuda contextual. Vive en el sidebar (arriba del usuario) y resuelve su
 * contenido según la ruta activa, así cada módulo muestra sus propios pasos
 * sin tocar cada página.
 *
 * Adentro, en este orden: primero los manuales en PDF (lo que más se busca),
 * después los pasos del módulo y sus advertencias. El manual de Nova sólo se
 * ofrece al perfil `nova`.
 */
export function HelpButton({ collapsed = false }: { collapsed?: boolean }) {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { profile } = useSession();
  const { code: pais } = useActiveCountry();
  const help = helpForPath(pathname);
  const esNova = profile?.perfil === 'nova';
  // Los pasos/notas exclusivos del otro país se ocultan: explicarle ARCA a
  // alguien parado en México (o el flujo Nova a alguien en Argentina) es ruido.
  const pasos = help ? pasosParaPais(help, pais) : [];
  const notas = help ? notasParaPais(help, pais) : [];

  return (
    <>
      {collapsed ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Ayuda"
          title="Ayuda"
          className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-lg bg-neutral-900 text-white transition-colors hover:bg-neutral-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <span className="text-base font-bold leading-none" aria-hidden>
            ?
          </span>
        </button>
      ) : (
        // Fila negra completa, alineada a la izquierda: mismas medidas que los
        // ítems del nav (gap-3 / px-3 / py-2.5) para que la lista se lea pareja.
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Ayuda"
          className="mb-2 flex w-full items-center gap-3 rounded-lg bg-neutral-900 px-3 py-2.5 text-sm font-medium text-white transition-colors hover:bg-neutral-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <span
            className="flex h-[18px] w-[18px] shrink-0 items-center justify-center text-base font-bold leading-none"
            aria-hidden
          >
            ?
          </span>
          Ayuda
        </button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        {/* 6xl (1152px) entra cómodo en 1440, que es la pantalla más chica del
            cliente. No más ancho que eso: con los pasos a dos columnas, más
            allá de ~1150 los renglones se vuelven demasiado largos para leer. */}
        <DialogContent className="max-w-6xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-neutral-900 text-xs font-bold text-white">
                ?
              </span>
              {help ? `Ayuda — ${help.titulo}` : 'Ayuda'}
            </DialogTitle>
            <DialogDescription>
              {help?.proposito || 'Manuales de uso de Fornitalia.'}
            </DialogDescription>
          </DialogHeader>

          <DialogBody className="space-y-5">
            {/* El manual primero: es lo que más se busca al abrir la ayuda.
                Un solo manual por perfil — Nova ve el suyo y NO el de
                vendedores, que no aplica a su flujo. */}
            <section className="space-y-2">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {esNova || pais !== 'AR' ? 'Manual' : 'Manuales'}
              </h3>
              <ManualLink {...(esNova ? MANUALES.nova : MANUALES.vendedores)} />
              {/* MercadoPago en cuotas y E-cheq existen sólo en AR. */}
              {!esNova && pais === 'AR' && <ManualLink {...MANUALES.mpEcheq} />}
              {/* La Factura E (exportación, ARCA) también es sólo AR. */}
              {!esNova && pais === 'AR' && <ManualLink {...MANUALES.facturaE} />}
              {/* Importaciones: sólo quienes la usan (admin y administración), en AR. */}
              {pais === 'AR' && (profile?.perfil === 'admin' || profile?.perfil === 'administracion') && (
                <ManualLink {...MANUALES.importaciones} />
              )}
            </section>

            {pasos.length > 0 && (
              <section className="space-y-2 border-t pt-4">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Cómo usar este módulo
                </h3>
                {/* Dos columnas en pantallas anchas para que entren más pasos
                    sin scrollear. */}
                <ol className="grid gap-2 sm:grid-cols-2 sm:gap-x-6">
                  {pasos.map((paso, i) => (
                    <li key={paso.titulo} className="flex gap-3">
                      <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-secondary text-[11px] font-semibold text-foreground">
                        {i + 1}
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-foreground">
                          {paso.titulo}
                        </span>
                        <span className="block text-sm text-muted-foreground">{paso.detalle}</span>
                      </span>
                    </li>
                  ))}
                </ol>
              </section>
            )}

            {notas.length > 0 && (
              <section className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5">
                <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-amber-900">
                  <Info className="h-3.5 w-3.5" aria-hidden /> Tené en cuenta
                </h3>
                <ul className="mt-1.5 space-y-1">
                  {notas.map((nota) => (
                    <li key={nota} className="text-sm text-amber-900">
                      • {nota}
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </DialogBody>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ManualLink({
  titulo,
  descripcion,
  href,
}: {
  titulo: string;
  descripcion: string;
  href: string;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="flex items-center gap-3 rounded-lg border px-3 py-2.5 transition-colors hover:bg-secondary"
    >
      <FileText className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden />
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium text-foreground">{titulo}</span>
        <span className="block text-xs text-muted-foreground">{descripcion}</span>
      </span>
      <ExternalLink className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
    </a>
  );
}
